import React, { useState } from 'react';
import { collection, doc, writeBatch } from 'firebase/firestore';
import { db } from '../firebase';
import { useNavigate } from 'react-router-dom';
import { useData } from '../context/DataContext';
import Sidebar, { SIDEBAR_W } from '../components/Sidebar';

const CATEGORY_MAP = {
  'Chain Link': 'Chain Link',
  'Galv Fittings': 'Galv Fittings',
  'Black Fittings': 'Black Fittings',
  'Galv Pipe': 'Galv Pipe',
  'Black Pipe': 'Black Pipe',
  'Residential Swing Gates Galv': 'Residential Swing Gates Galv',
  'Commercial Swing Gates Galv': 'Commercial Swing Gates Galv',
  'Residential Gates Black': 'Residential Gates Black',
  'Commercial Gates Black': 'Commercial Gates Black',
  'Wood': 'Wood',
  'Wood Hardware': 'Wood Hardware',
  'Split Rail': 'Split Rail',
  'Tools/Misc': 'Tools/Misc',
  'PVC': 'PVC',
  'Cantilever Gate': 'Cantilever Gate',
  'Aluminum Slide Gate Hardware': 'Aluminum Slide Gate Hardware',
  'Aluminum': 'Aluminum',
};

// Accepted header names per field (case-insensitive, trimmed). Supports both the
// short forms our export/CSV uses and common long forms.
const HEADER_ALIASES = {
  sku: ['sku'],
  description: ['desc', 'description'],
  category: ['cat', 'category'],
  cost: ['cost', 'cost price', 'costprice'],
  priceA: ['a', 'price a', 'pricea'],
  priceB: ['b', 'price b', 'priceb'],
  priceC: ['c', 'price c', 'pricec'],
  // Optional columns — only applied when present.
  unit: ['unit'],
  isStock: ['stock item', 'isstock', 'stock'],
  isActive: ['active', 'isactive'],
};

const REQUIRED_FIELDS = ['sku', 'description', 'category', 'cost', 'priceA', 'priceB', 'priceC'];
const FIELD_LABELS = {
  sku: 'SKU', description: 'Description (desc)', category: 'Category (cat)',
  cost: 'Cost (cost)', priceA: 'Price A (a)', priceB: 'Price B (b)', priceC: 'Price C (c)',
};

function cleanPrice(val) {
  if (val === undefined || val === null) return 0;
  return parseFloat(String(val).replace(/[$,\s]/g, '')) || 0;
}

// Parse a yes/no-style cell. Blank or unknown → default.
function parseBool(val, dflt) {
  if (val === undefined || val === null) return dflt;
  const s = String(val).trim().toLowerCase();
  if (s === '') return dflt;
  if (['yes', 'y', 'true', '1'].includes(s)) return true;
  if (['no', 'n', 'false', '0'].includes(s)) return false;
  return dflt;
}

// RFC-4180 CSV parser. Handles: quoted fields, commas & newlines inside quotes,
// doubled quotes ("") as a literal quote, \r\n or \n line endings, and a leading
// UTF-8 BOM. Returns an array of rows, each an array of string fields. Blank
// lines are dropped.
function parseCSV(text) {
  if (text.charCodeAt(0) === 0xFEFF) text = text.slice(1); // strip BOM
  const rows = [];
  let row = [];
  let field = '';
  let inQuotes = false;
  const n = text.length;
  for (let i = 0; i < n; i++) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') { field += '"'; i++; }  // doubled quote → literal "
        else { inQuotes = false; }
      } else {
        field += ch;  // preserve everything (incl. commas/newlines) inside quotes
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ',') {
      row.push(field); field = '';
    } else if (ch === '\n') {
      row.push(field); rows.push(row); row = []; field = '';
    } else if (ch === '\r') {
      // ignore CR (handles \r\n); a lone \r is treated as no-op
    } else {
      field += ch;
    }
  }
  row.push(field);
  rows.push(row);
  // Drop fully-blank lines (e.g. trailing newline → [''] ).
  return rows.filter(r => r.some(c => (c ?? '').trim() !== ''));
}

function ImportProducts({ user }) {
  const navigate = useNavigate();
  const { products, refreshProducts } = useData();
  const [status, setStatus] = useState('');
  const [progress, setProgress] = useState(0);
  const [total, setTotal] = useState(0);
  const [importing, setImporting] = useState(false);
  const [done, setDone] = useState(false);
  const [preview, setPreview] = useState([]);
  const [fileReady, setFileReady] = useState(false);
  const [parsedPlan, setParsedPlan] = useState([]);       // [{ action, id?, fields?, data?, sku, display }]
  const [summary, setSummary] = useState(null);           // { updateCount, createCount, skipCount, total }
  const [issues, setIssues] = useState(null);             // { skipped: [], priceWarnings: [] }

  const resetState = () => {
    setDone(false);
    setParsedPlan([]);
    setPreview([]);
    setSummary(null);
    setIssues(null);
    setFileReady(false);
    setProgress(0);
    setStatus('');
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    resetState();

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const rows = parseCSV(evt.target.result);
        if (rows.length < 2) {
          setStatus('❌ CSV has a header row but no data rows.');
          return;
        }

        // ── Header-based column mapping (by NAME, not position) ──
        const headers = rows[0].map(h => h.trim());
        const lower = headers.map(h => h.toLowerCase());
        const findIdx = (aliases) => lower.findIndex(h => aliases.includes(h));
        const idx = {};
        Object.keys(HEADER_ALIASES).forEach(key => { idx[key] = findIdx(HEADER_ALIASES[key]); });

        const missing = REQUIRED_FIELDS.filter(k => idx[k] === -1);
        if (missing.length > 0) {
          setStatus(
            `❌ Import aborted — missing required column(s): ${missing.map(k => FIELD_LABELS[k]).join(', ')}. ` +
            `Found headers: [${headers.join(', ')}]. ` +
            `Expected: sku; desc/description; cat/category; cost/cost price; a/price a; b/price b; c/price c.`
          );
          return;
        }

        const hasUnit = idx.unit !== -1;
        const hasStock = idx.isStock !== -1;
        const hasActive = idx.isActive !== -1;

        // Existing products keyed by normalized SKU (case-insensitive, trimmed).
        const bySku = new Map();
        products.forEach(p => { if (p.sku) bySku.set(String(p.sku).trim().toLowerCase(), p); });

        const plan = [];
        const skipped = [];
        const priceWarnings = [];
        let updateCount = 0;
        let createCount = 0;

        for (let r = 1; r < rows.length; r++) {
          const row = rows[r];
          const rowNum = r + 1; // 1-based, including header row
          const sku = (row[idx.sku] ?? '').trim();
          if (!sku) { skipped.push(`Row ${rowNum}: blank SKU — skipped`); continue; }

          const description = (row[idx.description] ?? '').trim();
          const rawCat = (row[idx.category] ?? '').trim();
          const category = CATEGORY_MAP[rawCat] || rawCat;

          const rawCost = (row[idx.cost] ?? '').trim();
          const costPrice = cleanPrice(rawCost);
          if (rawCost === '') priceWarnings.push(`Row ${rowNum} (${sku}): blank/invalid cost → 0`);
          const priceA = cleanPrice(row[idx.priceA]);
          const priceB = cleanPrice(row[idx.priceB]);
          const priceC = cleanPrice(row[idx.priceC]);
          // markup% is write-only (never read); guard against divide-by-zero on missing cost.
          const mk = (p) => (costPrice > 0 ? parseFloat((((p - costPrice) / costPrice) * 100).toFixed(1)) : 0);

          // Only fields actually present in the CSV are written on UPDATE.
          const fields = {
            description, category, costPrice, priceA, priceB, priceC,
            markupA: mk(priceA), markupB: mk(priceB), markupC: mk(priceC),
          };
          if (hasUnit) { const u = (row[idx.unit] ?? '').trim(); if (u) fields.unit = u; }
          if (hasStock) fields.isStock = parseBool(row[idx.isStock], true);
          if (hasActive) fields.isActive = parseBool(row[idx.isActive], true);

          const display = { sku, category, description, costPrice, priceA, priceB, priceC };
          const existing = bySku.get(sku.toLowerCase());
          if (existing) {
            // UPDATE: batch.update is a PARTIAL merge, so the doc ID and every
            // field the CSV does not carry survive untouched:
            //   packType, packQty, roundUpInQuotes, reorderPoint, stockQty,
            //   sortOrder, trackInventory, createdAt, and the SKU itself.
            //
            // roundUpInQuotes matters here as much as packQty does. It is the
            // owner's per-product decision that a quantity may be rounded up on
            // a customer's quote — set by hand in Inventory, never present in a
            // supplier's price list — so a routine price re-import must not be
            // able to clear it. A CREATE gets no roundUpInQuotes at all, which
            // reads as off: a brand new product never starts out rounding.
            plan.push({ action: 'update', id: existing.id, fields, sku, display });
            updateCount++;
          } else {
            // CREATE: CSV fields + defaults for anything the CSV doesn't provide.
            const data = {
              sku,
              ...fields,
              unit: fields.unit ?? 'pc',
              isStock: fields.isStock ?? true,
              isActive: fields.isActive ?? true,
              createdAt: new Date().toISOString(),
            };
            plan.push({ action: 'create', data, sku, display });
            createCount++;
          }
        }

        setParsedPlan(plan);
        setPreview(plan.slice(0, 10));
        setSummary({ updateCount, createCount, skipCount: skipped.length, total: plan.length });
        setIssues({ skipped, priceWarnings });
        setTotal(plan.length);
        setFileReady(plan.length > 0);
        setStatus(
          `✅ Parsed ${plan.length} row(s): ${updateCount} to update, ${createCount} new` +
          `${skipped.length ? `, ${skipped.length} skipped` : ''}. Review, then import.`
        );
      } catch (err) {
        console.error('Parse error:', err);
        setStatus(`❌ Failed to parse CSV: ${err.message}`);
      }
    };
    reader.readAsText(file);
  };

  const handleImport = async () => {
    if (parsedPlan.length === 0 || !summary) return;

    const proceed = window.confirm(
      `Import summary:\n\n` +
      `• ${summary.updateCount} existing product(s) will be UPDATED (prices/fields refreshed; ID, stock, packaging & all other data preserved)\n` +
      `• ${summary.createCount} new product(s) will be CREATED\n` +
      `• ${summary.skipCount} row(s) skipped (blank SKU)\n\n` +
      `Existing products NOT in this CSV will be left completely untouched.\n\nContinue?`
    );
    if (!proceed) return;

    setImporting(true);
    setDone(false);
    setProgress(0);

    try {
      let written = 0;
      let updated = 0;
      let created = 0;
      // Batched writes, chunked at Firestore's 500-op limit.
      for (let i = 0; i < parsedPlan.length; i += 500) {
        const chunk = parsedPlan.slice(i, i + 500);
        const batch = writeBatch(db);
        chunk.forEach(op => {
          if (op.action === 'update') {
            batch.update(doc(db, 'products', op.id), op.fields);
            updated++;
          } else {
            batch.set(doc(collection(db, 'products')), op.data);
            created++;
          }
        });
        await batch.commit();
        written += chunk.length;
        setProgress(written);
        setStatus(`Writing… ${written} / ${parsedPlan.length}`);
      }

      setStatus(
        `✅ Import complete — ${updated} updated, ${created} created` +
        `${summary.skipCount ? `, ${summary.skipCount} skipped` : ''}.`
      );
      setDone(true);
      refreshProducts();
    } catch (err) {
      console.error('Import error:', err);
      setStatus(`❌ Error during import: ${err.message}`);
    }

    setImporting(false);
  };

  return (
    <>
      <Sidebar />
      <div className="min-h-screen" style={{ marginLeft: SIDEBAR_W, backgroundColor: '#CDE7F5', fontFamily: 'Manrope, sans-serif' }}>

      <div className="max-w-4xl mx-auto p-6">

        {/* Slim header row */}
        <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
          <h1 className="text-xl font-bold" style={{ color: '#03045E' }}>📥 Import Products from CSV</h1>
          <button
            onClick={() => navigate('/pricelist')}
            className="text-sm font-semibold px-4 py-2 rounded-lg border transition"
            style={{ backgroundColor: '#E6F1FB', color: '#0077B6', borderColor: '#E3ECF5' }}
          >
            ← Price List
          </button>
        </div>

        <div className="rounded-xl p-6 mb-6" style={{ backgroundColor: '#FFFFFF', border: '1px solid #E3ECF5' }}>
          <p className="text-sm mb-2" style={{ color: '#6B7C93' }}>
            Upload a CSV with a header row. Columns are matched by <span className="font-semibold">name</span> (any order):
            {' '}<span className="font-mono">SKU, Description, Category, Cost, Price A, Price B, Price C</span> are required;
            {' '}<span className="font-mono">Unit, Stock Item, Active</span> are optional.
          </p>
          <p className="text-sm mb-6" style={{ color: '#0F6E56' }}>
            <span className="font-bold">Safe upsert:</span> rows are matched to existing products by SKU and updated in place.
            New SKUs are created. Products not in the CSV are left untouched — nothing is deleted.
          </p>

          {/* File Upload */}
          <div className="rounded-xl p-8 text-center mb-6" style={{ border: '2px dashed #0077B6', backgroundColor: '#E6F1FB' }}>
            <p className="mb-4" style={{ color: '#6B7C93' }}>Select your CSV file</p>
            <input
              type="file"
              accept=".csv"
              onChange={handleFileUpload}
              className="block mx-auto text-sm file:mr-4 file:py-2 file:px-6 file:rounded-lg file:border-0 file:font-bold file:bg-[#0077B6] file:text-white hover:file:bg-[#03045E]"
              style={{ color: '#6B7C93' }}
            />
          </div>

          {/* Status */}
          {status && (
            <div
              className="rounded-lg px-4 py-3 mb-6 font-semibold"
              style={
                status.includes('✅') ? { backgroundColor: '#E6F7EE', color: '#0F6E56' } :
                status.includes('❌') ? { backgroundColor: '#FBECEC', color: '#A32D2D' } :
                { backgroundColor: '#E6F1FB', color: '#0077B6' }
              }
            >
              {status}
            </div>
          )}

          {/* Summary counts */}
          {summary && !importing && (
            <div className="grid grid-cols-3 gap-3 mb-6">
              <div className="rounded-lg px-4 py-3 text-center" style={{ backgroundColor: '#E6F1FB' }}>
                <p className="text-2xl font-bold" style={{ color: '#0077B6' }}>{summary.updateCount}</p>
                <p className="text-xs font-semibold" style={{ color: '#6B7C93' }}>UPDATE EXISTING</p>
              </div>
              <div className="rounded-lg px-4 py-3 text-center" style={{ backgroundColor: '#E6F7EE' }}>
                <p className="text-2xl font-bold" style={{ color: '#0F6E56' }}>{summary.createCount}</p>
                <p className="text-xs font-semibold" style={{ color: '#6B7C93' }}>CREATE NEW</p>
              </div>
              <div className="rounded-lg px-4 py-3 text-center" style={{ backgroundColor: '#FBECEC' }}>
                <p className="text-2xl font-bold" style={{ color: '#A32D2D' }}>{summary.skipCount}</p>
                <p className="text-xs font-semibold" style={{ color: '#6B7C93' }}>SKIPPED</p>
              </div>
            </div>
          )}

          {/* Progress Bar */}
          {importing && (
            <div className="mb-6">
              <div className="flex justify-between text-sm mb-1" style={{ color: '#6B7C93' }}>
                <span>Importing...</span>
                <span>{progress} / {total}</span>
              </div>
              <div className="w-full rounded-full h-4" style={{ backgroundColor: '#E6F1FB' }}>
                <div
                  className="h-4 rounded-full transition-all"
                  style={{ width: `${total > 0 ? (progress / total) * 100 : 0}%`, backgroundColor: '#0077B6' }}
                />
              </div>
            </div>
          )}

          {/* Issues (skipped rows / price warnings) */}
          {issues && (issues.skipped.length > 0 || issues.priceWarnings.length > 0) && (
            <div className="mb-6 rounded-lg px-4 py-3" style={{ backgroundColor: '#FDF1DD', border: '1px solid #F0D9A8' }}>
              <p className="text-sm font-bold mb-1" style={{ color: '#854F0B' }}>
                ⚠️ {issues.skipped.length + issues.priceWarnings.length} row issue(s) — import will proceed for valid rows:
              </p>
              <ul className="text-xs list-disc pl-5" style={{ color: '#854F0B' }}>
                {[...issues.skipped, ...issues.priceWarnings].slice(0, 20).map((msg, i) => (
                  <li key={i}>{msg}</li>
                ))}
                {(issues.skipped.length + issues.priceWarnings.length) > 20 && (
                  <li>…and {(issues.skipped.length + issues.priceWarnings.length) - 20} more</li>
                )}
              </ul>
            </div>
          )}

          {/* Preview */}
          {preview.length > 0 && (
            <div className="mb-6">
              <h3 className="font-bold mb-3" style={{ color: '#03045E' }}>Preview (first 10 rows):</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead style={{ backgroundColor: '#E6F1FB' }}>
                    <tr style={{ color: '#03045E' }}>
                      <th className="px-3 py-2 text-left">Action</th>
                      <th className="px-3 py-2 text-left">SKU</th>
                      <th className="px-3 py-2 text-left">Category</th>
                      <th className="px-3 py-2 text-left">Description</th>
                      <th className="px-3 py-2 text-right">Cost</th>
                      <th className="px-3 py-2 text-right">Price A</th>
                      <th className="px-3 py-2 text-right">Price B</th>
                      <th className="px-3 py-2 text-right">Price C</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.map((item, i) => (
                      <tr key={i} style={{ backgroundColor: i % 2 === 0 ? '#FFFFFF' : '#CDE7F5' }}>
                        <td className="px-3 py-1.5">
                          <span className="font-bold px-2 py-0.5 rounded text-xs" style={
                            item.action === 'update'
                              ? { backgroundColor: '#E6F1FB', color: '#0077B6' }
                              : { backgroundColor: '#E6F7EE', color: '#0F6E56' }
                          }>
                            {item.action === 'update' ? 'UPDATE' : 'NEW'}
                          </span>
                        </td>
                        <td className="px-3 py-1.5 font-mono font-bold" style={{ color: '#0077B6' }}>{item.display.sku}</td>
                        <td className="px-3 py-1.5" style={{ color: '#6B7C93' }}>{item.display.category}</td>
                        <td className="px-3 py-1.5" style={{ color: '#03045E' }}>{item.display.description}</td>
                        <td className="px-3 py-1.5 text-right" style={{ color: '#03045E' }}>${item.display.costPrice.toFixed(2)}</td>
                        <td className="px-3 py-1.5 text-right" style={{ color: '#0077B6' }}>${item.display.priceA.toFixed(2)}</td>
                        <td className="px-3 py-1.5 text-right" style={{ color: '#0F6E56' }}>${item.display.priceB.toFixed(2)}</td>
                        <td className="px-3 py-1.5 text-right" style={{ color: '#854F0B' }}>${item.display.priceC.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Import Button */}
          {fileReady && !done && (
            <button
              onClick={handleImport}
              disabled={importing}
              className="font-bold px-8 py-3 rounded-xl text-lg transition disabled:opacity-50 w-full"
              style={{ backgroundColor: '#FDF1DD', color: '#854F0B', border: '1px solid #854F0B' }}
            >
              {importing
                ? `Importing ${progress}/${total}...`
                : `🚀 Import ${total} Row(s) — ${summary ? summary.updateCount : 0} update, ${summary ? summary.createCount : 0} new`}
            </button>
          )}

          {done && (
            <div className="flex gap-4">
              <button onClick={() => navigate('/pricelist')}
                className="font-bold px-8 py-3 rounded-xl text-lg transition flex-1"
                style={{ backgroundColor: '#0077B6', color: '#FFFFFF' }}>
                ✅ Go to Price List
              </button>
            </div>
          )}
        </div>
      </div>
      </div>
    </>
  );
}

export default ImportProducts;
